import { useCallback, useState } from 'react';
import { pullKinnser } from '../../services/api';
import type { KinnserPullSummary } from '../../services/api/types.gen';
import { formatDateTimeLocal } from '../../utils/dateUtils';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

/**
 * Pulls an agency's clinic info from Kinnser and saves it on its demo config.
 *
 * A pull writes only the config; the next run writes it to the agency's tables
 * and never calls Kinnser itself. Both the settings dialog and the run dialog
 * need this.
 */
export function usePullKinnser() {
  const [pulling, setPulling] = useState(false);
  const [result, setResult] = useState<KinnserPullSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The new summary, or null when the pull failed.
  const pull = useCallback(
    async (agencyId: string): Promise<KinnserPullSummary | null> => {
      setPulling(true);
      setError(null);
      try {
        const res = await pullKinnser({
          path: { agency_id: agencyId },
          throwOnError: true,
        });
        setResult(res.data ?? null);
        return res.data ?? null;
      } catch (caught) {
        console.error('Failed to pull from Kinnser', caught);
        setError(
          getBusinessErrorMessage(caught) ??
            'Unable to pull from Kinnser. Please try again.'
        );
        return null;
      } finally {
        setPulling(false);
      }
    },
    []
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { pull, pulling, result, error, reset };
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** "Pulled 9/11/2026, 10:42 AM: 1 clinic, 29 insurances, 885 physicians, 40 staff." */
export function formatPullSummary(pull: KinnserPullSummary): string {
  return `Pulled ${formatDateTimeLocal(pull.pulled_at)}: ${plural(
    pull.clinics,
    'clinic'
  )}, ${plural(pull.insurances, 'insurance')}, ${plural(
    pull.physicians,
    'physician'
  )}, ${pull.staff ?? 0} staff.`;
}
